import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, TrendingUp, Users, Zap, BarChart3, Briefcase, Rocket, ShieldCheck, Heart } from 'lucide-react';
import AnimatedNumber from './AnimatedNumber';

const HeroSection = () => {
  return (
    <section id="top" className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
      <div className="aurora-bg" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* LEFT */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="mb-6"
            >
              <span className="eyebrow">
                <Sparkles size={14} className="text-[#4facfe]" />
                Quando os dois se conectam, nasce uma Spark.
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="heading-display text-4xl sm:text-5xl lg:text-7xl text-white"
            >
              Conectamos <span className="gradient-text-blue">investidores</span>{' '}
              <br className="hidden sm:block" />
              a <span className="gradient-text-gold">pequenos negócios</span>.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25 }}
              className="mt-6 text-base sm:text-lg text-slate-300/90 max-w-xl leading-relaxed"
            >
              Apresente sua ideia, encontre investidores e transforme seu projeto em realidade.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/auth?signup=1&role=entrepreneur"
                data-testid="hero-cta-entrepreneur"
                className="btn-primary rounded-2xl px-7 py-4 text-sm inline-flex items-center gap-2 group"
              >
                Procurar Investidor
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/auth?signup=1&role=investor"
                data-testid="hero-cta-investor"
                className="btn-gold rounded-2xl px-7 py-4 text-sm inline-flex items-center gap-2 group"
              >
                Quero Investir
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>

            {/* Trust pillars (institutional, no fake numbers until we have real data) */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.55 }}
              className="mt-12 grid grid-cols-3 gap-3 sm:gap-5 max-w-2xl"
              data-testid="hero-trust-pillars"
            >
              {[
                {
                  icon: ShieldCheck,
                  title: '100% Gratuito',
                  desc: 'Cadastro sem cobranças até sua primeira Spark.',
                  color: 'blue',
                },
                {
                  icon: Users,
                  title: 'Conexão Direta',
                  desc: 'Sem intermediários entre você e o outro lado.',
                  color: 'gold',
                },
                {
                  icon: Heart,
                  title: 'Negócios Reais',
                  desc: 'Pequenos empreendedores e histórias verdadeiras.',
                  color: 'blue',
                },
              ].map((p, i) => (
                <div key={i} className="glass-card rounded-2xl p-4 sm:p-5">
                  <p.icon size={18} className={p.color === 'gold' ? 'text-[#ffb74d]' : 'text-[#4facfe]'} />
                  <div className="mt-3 text-sm sm:text-base font-bold text-white leading-tight">{p.title}</div>
                  <div className="text-[11px] sm:text-xs text-slate-400 mt-1 leading-snug">{p.desc}</div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* RIGHT — Illustration */}
          <div className="lg:col-span-5">
            <HeroIllustration />
          </div>
        </div>
      </div>
    </section>
  );
};

const HeroIllustration = () => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.92, y: 30 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
      className="relative aspect-square w-full max-w-lg mx-auto"
    >
      {/* Outer glow ring */}
      <div className="absolute inset-6 rounded-full pulse-glow" style={{
        background: 'conic-gradient(from 0deg, rgba(79, 172, 254, 0.15), rgba(255, 167, 38, 0.15), rgba(79, 172, 254, 0.15))',
        filter: 'blur(40px)'
      }} />

      {/* Rotating ring */}
      <svg className="absolute inset-0 w-full h-full spin-slow" viewBox="0 0 400 400">
        <defs>
          <linearGradient id="ring-grad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#4facfe" stopOpacity="0.7" />
            <stop offset="50%" stopColor="#ffffff" stopOpacity="0.1" />
            <stop offset="100%" stopColor="#ffa726" stopOpacity="0.7" />
          </linearGradient>
        </defs>
        <circle cx="200" cy="200" r="175" fill="none" stroke="url(#ring-grad)" strokeWidth="1" />
        <circle cx="200" cy="200" r="140" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="1" strokeDasharray="2 6" />
      </svg>

      {/* SVG connections */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 400" style={{ pointerEvents: 'none' }}>
        <defs>
          <linearGradient id="line-grad-1" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#4facfe" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#ffa726" stopOpacity="0.8" />
          </linearGradient>
        </defs>
        <path className="dashed-flow" d="M 80 100 Q 200 180 320 90" fill="none" stroke="url(#line-grad-1)" strokeWidth="1.5" />
        <path className="dashed-flow" d="M 80 300 Q 200 220 320 310" fill="none" stroke="url(#line-grad-1)" strokeWidth="1.5" />
        <path className="dashed-flow" d="M 80 100 Q 80 200 80 300" fill="none" stroke="#4facfe" strokeOpacity="0.4" strokeWidth="1" />
        <path className="dashed-flow" d="M 320 90 Q 320 200 320 310" fill="none" stroke="#ffa726" strokeOpacity="0.4" strokeWidth="1" />
      </svg>

      {/* Central spark card */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.7 }}
        className="absolute inset-0 flex items-center justify-center"
      >
        <div className="glass-card rounded-2xl px-6 py-5 glow-mix float-med" style={{ width: '180px' }}>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="text-[10px] font-semibold tracking-widest text-slate-400">SPARK</span>
          </div>
          <div className="text-3xl heading-display font-bold gradient-text">98%</div>
          <div className="text-xs text-slate-400 mt-1">Compatibilidade</div>
          <div className="mt-3 h-1 rounded-full bg-white/5 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: '98%' }}
              transition={{ duration: 1.4, delay: 1.2, ease: 'easeOut' }}
              className="h-full rounded-full"
              style={{ background: 'linear-gradient(90deg, #4facfe, #ffa726)' }}
            />
          </div>
        </div>
      </motion.div>

      {/* Floating nodes */}
      <FloatingNode icon={Briefcase} label="Quero Investir" sub="R$ 5.000" color="blue" position="top-4 left-2" delay={0.4} float="float-slow" />
      <FloatingNode icon={Rocket} label="Tenho uma ideia" sub="Pequeno negócio" color="gold" position="top-4 right-2" delay={0.55} float="float-med" />
      <FloatingNode icon={BarChart3} label="Crescendo" sub="+ vendas" color="blue" position="bottom-6 left-2" delay={0.7} float="float-fast" />
      <FloatingNode icon={Users} label="Conexões" sub="12 novas" color="gold" position="bottom-6 right-2" delay={0.85} float="float-slow" />
    </motion.div>
  );
};

const FloatingNode = ({ icon: Icon, label, sub, color, position, delay, float }) => {
  const isGold = color === 'gold';
  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.85 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.6, delay }}
      className={`absolute ${position} ${float}`}
    >
      <div className={`glass-card rounded-xl px-3 py-2.5 flex items-center gap-2.5 ${isGold ? 'glow-gold' : 'glow-blue'}`}>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
          isGold ? 'bg-[#ffa726]/15 text-[#ffb74d]' : 'bg-[#4facfe]/15 text-[#4facfe]'
        }`}>
          <Icon size={16} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-400 leading-none">{label}</div>
          <div className="text-sm font-bold text-white leading-tight mt-0.5">{sub}</div>
        </div>
      </div>
    </motion.div>
  );
};

export default HeroSection;
